export async function convertSvgToPngBlob(
  svgElement: SVGSVGElement
): Promise<Blob | null> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      resolve(null);
      return;
    }

    const svgData = prepareSvgForExport(svgElement);
    const img = new Image();
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      try {
        const svgRect = svgElement.getBoundingClientRect();
        const exportWidth = Math.max(Math.floor(svgRect.width), img.width || 1);
        const exportHeight = Math.max(Math.floor(svgRect.height), img.height || 1);

        canvas.width = exportWidth;
        canvas.height = exportHeight;

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          resolve(blob);
        });
      } catch (err) {
        URL.revokeObjectURL(url);
        console.error('Error during canvas image draw:', err);
        resolve(null);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      console.error('Failed to load SVG as image.');
      resolve(null);
    };

    img.src = url;
  });
}

/**
 * Check if the browser supports clipboard API for images.
 */
export function supportsImageClipboard(): boolean {
  const clipboard = navigator?.clipboard as Clipboard | undefined;
  return !!clipboard && 
         typeof clipboard.write === 'function' && 
         typeof (window as any).ClipboardItem !== 'undefined';
}

/**
 * Copy PNG blob to clipboard.
 * Returns true if successful, false otherwise.
 */
export async function copyBlobToClipboard(blob: Blob): Promise<boolean> {
  try {
    const clipboard = navigator?.clipboard as Clipboard | undefined;
    if (!clipboard) {
      return false;
    }

    await clipboard.write([
      new (window as any).ClipboardItem({
        'image/png': blob,
      })
    ]);
    return true;
  } catch (err) {
    console.error('Clipboard write error:', err);
    return false;
  }
}

/**
 * Prepare SVG for export by cloning and removing backgrounds.
 */
function prepareSvgForExport(sourceSvg: SVGSVGElement): string {
  const exportSvg = sourceSvg.cloneNode(true) as SVGSVGElement;
  exportSvg.style.background = 'transparent';
  exportSvg.style.backgroundColor = 'transparent';

  return new XMLSerializer().serializeToString(exportSvg);
}

/**
 * Copy schematic SVG to clipboard as PNG image.
 * Returns an object with success status and message.
 */
export async function copySchematicToClipboard(svgElement: SVGSVGElement): Promise<{ success: boolean; message: string }> {
  if (!svgElement) {
    return { success: false, message: 'Schematic is not ready to copy yet.' };
  }

  if (!supportsImageClipboard()) {
    return { success: false, message: 'Clipboard API not supported in your browser.' };
  }

  try {
    const blob = await convertSvgToPngBlob(svgElement);
    if (!blob) {
      return { success: false, message: 'Failed to convert schematic to image.' };
    }

    const success = await copyBlobToClipboard(blob);
    if (success) {
      return { success: true, message: 'Schematic copied to clipboard.' };
    } else {
      return { success: false, message: 'Failed to copy schematic to clipboard.' };
    }
  } catch (err) {
    console.error('Copy schematic error:', err);
    return { success: false, message: 'An error occurred while copying the schematic.' };
  }
}
